import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { Button, Dropdown, Grid } from "antd";
import { PlusOutlined, CloseOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "../../stores/authStore";
import { useCurrentUserPrivileges, useRemoveQuickAction } from "../../hooks/useUserPrivileges";
import navigationConfig from "../../config/navigationConfig.json";
import iconMap from "../../config/iconMap";

// Build a path -> section icon lookup and path -> privilegeKey lookup
const pathToSectionIcon = {};
const pathToPrivilegeKey = {};
navigationConfig.sections.forEach((section) => {
  section.items.forEach((item) => {
    pathToSectionIcon[item.path] = section.icon;
    if (item.privilegeKey) pathToPrivilegeKey[item.path] = item.privilegeKey;
    if (item.children) {
      item.children.forEach((child) => {
        pathToSectionIcon[child.path] = section.icon;
        if (child.privilegeKey) pathToPrivilegeKey[child.path] = child.privilegeKey;
      });
    }
  });
});

const getIconForPath = (path) => {
  const sectionKey = pathToSectionIcon[path];
  return sectionKey ? iconMap[sectionKey] : null;
};

const QuickActions = () => {
  const navigate = useNavigate();
  const screens = Grid.useBreakpoint();
  const { user } = useAuthStore();
  const userId = user?.id;

  const { quickActions, hasPrivilege } = useCurrentUserPrivileges();
  const { mutate: removeAction } = useRemoveQuickAction();

  const [confirmPath, setConfirmPath] = useState(null);
  const confirmRef = useRef(null);

  // Filter quick actions by privilege - only show actions the user has access to
  const visibleActions = useMemo(() => {
    return quickActions.filter((action) => {
      const privilegeKey = pathToPrivilegeKey[action.path];
      // If no privilegeKey mapping, always show (e.g. dashboard)
      if (!privilegeKey) return true;
      return hasPrivilege(privilegeKey);
    });
  }, [quickActions, hasPrivilege]);

  // Close confirm popup on outside click
  useEffect(() => {
    if (confirmPath === null) return;
    const handler = (e) => {
      if (confirmRef.current && !confirmRef.current.contains(e.target)) {
        setConfirmPath(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [confirmPath]);

  const handleRemove = useCallback((path) => {
    if (userId) {
      removeAction({ userId, path });
      setConfirmPath(null);
    }
  }, [userId, removeAction]);

  if (visibleActions.length === 0) {
    return <span className="quick-actions-empty">Right-click sidebar to add</span>;
  }

  const menuItems = visibleActions.map((action) => ({
    key: action.key,
    label: action.label,
    icon: getIconForPath(action.path),
    onClick: () => navigate(action.path),
  }));

  if (screens.xl) {
    return (
      <div className="quick-actions-desktop">
        {visibleActions.map((action) => (
          <div key={action.key} style={{ position: "relative" }}>
            <button
              className="quick-action-pill"
              onClick={() => navigate(action.path)}
            >
              {getIconForPath(action.path)}
              <span>{action.label}</span>
              <span
                className="quick-action-remove"
                onClick={(e) => {
                  e.stopPropagation();
                  setConfirmPath(action.path);
                }}
                title="Remove"
              >
                <CloseOutlined />
              </span>
            </button>
            {confirmPath === action.path && (
              <div className="quick-action-confirm" ref={confirmRef}>
                <span className="quick-action-confirm-text">Remove?</span>
                <div className="quick-action-confirm-buttons">
                  <button
                    className="quick-action-confirm-btn confirm-no"
                    onClick={() => setConfirmPath(null)}
                  >
                    No
                  </button>
                  <button
                    className="quick-action-confirm-btn confirm-yes"
                    onClick={() => handleRemove(action.path)}
                  >
                    Yes
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    );
  }

  return (
    <Dropdown
      menu={{ items: menuItems }}
      trigger={["click"]}
      placement="bottomRight"
    >
      <Button
        type="text"
        className="quick-action-plus-btn"
        icon={<PlusOutlined />}
      />
    </Dropdown>
  );
};

export default QuickActions;
